import { useModal } from "./useModal";
import { t } from "@/services/i18n";
let hasOpenAlertBasicModal = false;

export const useAlertBasic = () => {
  const modalOption = { backdrop: true, keyboard: true, focus: true };
  const modalFactory = useModal(modalOption);
  const _createTitleDom = (title: string) => {
    const dom = document.createElement("div");
    dom.classList.add("flex-wrap");
    dom.classList.add("d-flex");
    dom.classList.add("align-items-center");
    dom.classList.add("justify-content-between");
    dom.classList.add("p-8");
    dom.classList.add("text-white");
    dom.classList.add("rounded-3");
    dom.style.backgroundColor = "#343434";
    dom.style.fontWeight = "600";
    const titleDom = document.createElement("div");
    titleDom.innerHTML = title;
    const closeBtnDom = modalFactory.createCloseButton(
      "<i class='via-icon-close' />"
    );
    closeBtnDom.classList.add("btn");
    closeBtnDom.classList.add("text-white");
    closeBtnDom.classList.add("p-0");
    const closeBtnBoxDom = document.createElement("div");
    closeBtnBoxDom.classList.add("flex-fill");
    closeBtnBoxDom.classList.add("d-flex");
    closeBtnBoxDom.classList.add("justify-content-end");
    closeBtnBoxDom.appendChild(closeBtnDom);
    dom.appendChild(titleDom);
    dom.appendChild(closeBtnBoxDom);
    return { dom, closeBtnDom };
  };

  const _createContentDom = (content: string) => {
    const dom = document.createElement("div");
    dom.classList.add("text-start");
    dom.classList.add("pt-16");
    dom.classList.add("pb-16");
    dom.classList.add("text-break");
    dom.classList.add("px-16");
    dom.style.minHeight = "4rem";
    dom.innerText = content;
    return dom;
  };

  const _createAlertFooter = (content: string) => {
    const dom = document.createElement("div");
    dom.classList.add("d-flex");
    dom.classList.add("justify-content-around");
    dom.classList.add("p-8");
    dom.classList.add("g-16");
    const confirmBtnDom = modalFactory.createCloseButton("Yes");
    confirmBtnDom.style.minWidth = "10rem";
    confirmBtnDom.classList.add("btn");
    confirmBtnDom.classList.add("via-btn-save");
    confirmBtnDom.classList.add("w-50");
    confirmBtnDom.innerText = content;
    dom.appendChild(confirmBtnDom);
    return dom;
  };

  const _createConfirmFooter = (
    confirmText: string,
    cancelText: string,
    showCancelButton: boolean
  ) => {
    const dom = document.createElement("div");
    dom.style.gap = "1rem";
    dom.classList.add("d-flex");
    dom.classList.add("flex-wrap");
    dom.classList.add("justify-content-around");
    dom.classList.add("p-8");
    dom.classList.add("g-16");
    const confirmBtnDom = modalFactory.createCloseButton("Yes");
    if (showCancelButton) {
      confirmBtnDom.style.flex = "1";
    }
    confirmBtnDom.classList.add("btn");
    confirmBtnDom.classList.add("via-btn-save");
    confirmBtnDom.innerText = confirmText;
    const cancelBtnDom = modalFactory.createCloseButton("Yes");
    cancelBtnDom.style.flex = "1";
    cancelBtnDom.classList.add("btn");
    cancelBtnDom.classList.add("via-btn-cancel");
    cancelBtnDom.innerText = cancelText;
    if (!showCancelButton) {
      cancelBtnDom.style.display = "none";
    }
    dom.appendChild(confirmBtnDom);
    dom.appendChild(cancelBtnDom);
    return { dom, confirmBtnDom, cancelBtnDom };
  };

  const alert = (
    content: string,
    title = t("common.global.warning"),
    confirmText = t("common.global.ok")
  ) => {
    if (hasOpenAlertBasicModal) {
      return Promise.reject("Can not open alert basic modal more then once.");
    }

    hasOpenAlertBasicModal = true;

    type Resolve = <T>(value?: T) => void;
    let _resolve: Resolve | undefined = undefined;
    const resolve = () => _resolve && _resolve();
    const modalDom = modalFactory.createModalDom({
      addHeader: false,
      addBody: true,
      addFooter: false,
    });
    modalFactory.setModalDom(modalDom, {
      centered: true,
      static: true,
      fade: true,
    });
    const headerDom = _createTitleDom(title);
    const contentDom = _createContentDom(content);
    const footerDom = _createAlertFooter(confirmText);
    modalDom.modalBody.style.padding = "0.25rem 0.25rem 0.5rem 0.25rem";
    modalDom.modalBody.appendChild(headerDom.dom);
    modalDom.modalBody.appendChild(contentDom);
    modalDom.modalBody.appendChild(footerDom);
    modalDom.modal.addEventListener("hide.bs.modal", () => {
      hasOpenAlertBasicModal = false;
      resolve();
    });
    const modal = modalFactory.createModal(modalDom);
    modal.show();
    return new Promise((resolve) => {
      _resolve = resolve;
    });
  };

  const confirm = (
    content: string,
    title = t("common.global.warning"),
    confirmText = t("common.global.ok"),
    cancelText = t("common.global.cancel"),
    callback?: {
      yesClickCallback?: () => Promise<string>;
      cancelClickCallback?: () => Promise<string>;
    },
    options?: {
      showCancelButton: boolean;
    }
  ) => {
    if (hasOpenAlertBasicModal) {
      return Promise.reject("Can not open alert basic modal more then once.");
    }

    hasOpenAlertBasicModal = true;

    type Resolve = <T>(value?: T) => void;
    type Reject = <T>(value?: T) => void;
    let _resolve: Resolve | undefined = undefined;
    let _reject: Reject | undefined = undefined;
    const resolve = (val?: string) => _resolve && _resolve(val);
    const reject = (val?: string) => _reject && _reject(val);
    const modalDom = modalFactory.createModalDom({
      addHeader: false,
      addBody: true,
      addFooter: false,
    });
    modalFactory.setModalDom(modalDom, {
      centered: true,
      static: true,
      fade: true,
    });
    const headerDom = _createTitleDom(title);
    const contentDom = _createContentDom(content);
    const footerDom = _createConfirmFooter(
      confirmText,
      cancelText,
      options?.showCancelButton ?? true
    );
    modalDom.modalBody.style.padding = "0.25rem 0.25rem 0.5rem 0.25rem";
    modalDom.modalBody.appendChild(headerDom.dom);
    modalDom.modalBody.appendChild(contentDom);
    modalDom.modalBody.appendChild(footerDom.dom);
    headerDom.closeBtnDom.addEventListener("click", () => {
      //
      reject();
    });
    footerDom.cancelBtnDom.addEventListener("click", async () => {
      try {
        if (callback && callback.cancelClickCallback) {
          resolve(await callback.cancelClickCallback());
        }
        reject();
      } catch (error) {
        //
      }
    });
    footerDom.confirmBtnDom.addEventListener("click", async () => {
      try {
        if (callback && callback.yesClickCallback) {
          resolve(await callback.yesClickCallback());
        }
        resolve();
      } catch (error) {
        //
      }
    });
    // modalDom.modal.addEventListener("hidden.bs.modal", () => {
    //   hasOpenAlertBasicModal = false;
    //   reject();
    // });
    modalDom.modal.addEventListener("hide.bs.modal", () => {
      hasOpenAlertBasicModal = false;
      // reject();
    });
    const modal = modalFactory.createModal(modalDom);
    modal.show();
    return new Promise((resolve, reject) => {
      _resolve = resolve;
      _reject = reject;
    });
  };

  const confirmStep = async (
    content: string,
    title = t("common.global.warning"),
    previousText = t("common.global.ok"),
    nextText = t("common.global.cancel")
  ) => {
    return confirm(content, title, previousText, nextText, {
      yesClickCallback() {
        return Promise.resolve("previous");
      },
      cancelClickCallback() {
        return Promise.resolve("next");
      },
    });
  };

  return { alert, confirm, confirmStep };
};
